/**
 * Shared presentational pieces for the homepage sections.
 *
 * The visual language here is the practice map's, not the old homepage's: white
 * surfaces with a hairline border (`cardSx`), the neutral slate ink ramp rather
 * than the theme's navy `text.primary`, and quiet pastel icons on a near-white
 * tile. `mapTokens.js` documents why each of those tones was chosen — this module
 * just applies them so the homepage and the app look like one product.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { BRAND, ICON_QUIET, ICON_TILE, INK, MUTED, SURFACE } from '../../pages/Practice/map/mapTokens';
import { accentRuleSx, BAND_PLAIN, BAND_TINT, CONTENT_MAX_WIDTH, SOFT_BLUE, TEXT_MAX_WIDTH } from '../../styles/tokens';
import { ACCESS, CHAPTERS } from './homeContent';

/*
 * Icons are registered explicitly, one named import each.
 *
 * `import * as MuiIcons from '@mui/icons-material'` with a dynamic `MuiIcons[name]`
 * lookup would be far tidier, but CRA cannot tree-shake it — the whole icon
 * library (several thousand components) ends up in the bundle. On the page whose
 * job is to convert a first-time visitor, that is the last place to spend
 * kilobytes, so the registry is written out by hand.
 */
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Category from '@mui/icons-material/Category';
import Check from '@mui/icons-material/Check';
import CircleOutlined from '@mui/icons-material/CircleOutlined';
import Description from '@mui/icons-material/Description';
import Drafts from '@mui/icons-material/Drafts';
import EditNote from '@mui/icons-material/EditNote';
import Explore from '@mui/icons-material/Explore';
import Forum from '@mui/icons-material/Forum';
import ImageIcon from '@mui/icons-material/Image';
import Lightbulb from '@mui/icons-material/Lightbulb';
import Lock from '@mui/icons-material/Lock';
import MarkEmailUnread from '@mui/icons-material/MarkEmailUnread';
import MenuBook from '@mui/icons-material/MenuBook';
import Mic from '@mui/icons-material/Mic';
import Phone from '@mui/icons-material/Phone';
import PlayCircle from '@mui/icons-material/PlayCircle';
import QuestionAnswer from '@mui/icons-material/QuestionAnswer';
import RateReview from '@mui/icons-material/RateReview';
import RecordVoiceOver from '@mui/icons-material/RecordVoiceOver';
import Sms from '@mui/icons-material/Sms';
import Stairs from '@mui/icons-material/Stairs';
import Style from '@mui/icons-material/Style';
import Timer from '@mui/icons-material/Timer';
import TipsAndUpdates from '@mui/icons-material/TipsAndUpdates';

const ICONS = {
  CalendarMonth,
  Category,
  Check,
  Description,
  Drafts,
  EditNote,
  Explore,
  Forum,
  Image: ImageIcon,
  Lightbulb,
  Lock,
  MarkEmailUnread,
  MenuBook,
  Mic,
  Phone,
  PlayCircle,
  QuestionAnswer,
  RateReview,
  RecordVoiceOver,
  Sms,
  Stairs,
  Style,
  Timer,
  TipsAndUpdates,
};

/**
 * Content modules name their icon as a string so the copy stays free of JSX.
 * Falling back to a neutral glyph keeps a typo from crashing the page.
 */
export const HomeIcon = ({ name, ...props }) => {
  const Icon = ICONS[name] ?? CircleOutlined;
  return <Icon {...props} />;
};

/**
 * A pastel glyph on a near-white tile. ICON_SURFACE is itself pastel blue, so a
 * pastel icon on it drops to roughly 1.5:1 and disappears — hence ICON_TILE.
 */
export const IconTile = ({ name, size = 44, iconSize = 22, color = ICON_QUIET, iconClassName, sx }) => (
  <Box
    aria-hidden
    sx={{
      width: size,
      height: size,
      flex: '0 0 auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '12px',
      bgcolor: SOFT_BLUE,
      color,
      ...sx,
    }}
  >
    <HomeIcon name={name} className={iconClassName} sx={{ fontSize: iconSize }} />
  </Box>
);

/**
 * Every section heading carries the same short red rule. Repeating it in one
 * consistent structural role is what turns the logo's red from an outlier into
 * part of the system — see ACCENT_RED in styles/tokens.
 *
 * Left-aligned by default. The rule needs a hard edge to sit against to read as
 * a deliberate mark rather than a stray dash, and left-aligning every heading
 * gives the whole page one continuous vertical edge — starting at the hero,
 * whose rule was already left-aligned. Centred headings also force the eye back
 * to the middle on each section, which is slower to scan than a fixed left
 * start point.
 */
export const SectionHeading = ({ title, subtitle, align = 'left', contentWidth = CONTENT_MAX_WIDTH, chapter, sx }) => (
  <Box
    sx={{
      textAlign: align,
      // Matches the width of the grid below it, so the heading, the rule and the
      // first card all start on the same vertical line.
      maxWidth: align === 'center' ? 760 : contentWidth,
      mx: 'auto',
      mb: { xs: 4, md: 6 },
      ...sx,
    }}
  >
    {chapter ? <ChapterChip chapter={chapter} /> : null}
    <Typography variant="h3" component="h2" fontWeight={900} color="primary.main" sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    <Box aria-hidden sx={{ ...accentRuleSx, mx: align === 'center' ? 'auto' : 0, mb: subtitle ? 2 : 0 }} />
    {subtitle ? (
      <Typography
        sx={{ color: MUTED, fontSize: { xs: '1rem', md: '1.125rem' }, lineHeight: 1.7, maxWidth: TEXT_MAX_WIDTH }}
      >
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

/**
 * Marks a section as the answer to one of the three StartHere doors.
 *
 * Same label, icon and accent as the card the visitor clicked, so the choice
 * they made at the top of the page stays visible as they scroll instead of the
 * sections reading as an unrelated run.
 */
export const ChapterChip = ({ chapter }) => {
  const entry = CHAPTERS[chapter];
  if (!entry) return null;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        mb: 1.5,
        borderRadius: '999px',
        bgcolor: SOFT_BLUE,
        color: entry.accent,
        fontSize: '0.6875rem',
        fontWeight: 900,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
      }}
    >
      <HomeIcon name={entry.icon} aria-hidden sx={{ fontSize: 14 }} />
      {entry.label}
    </Box>
  );
};

const badgeTone = {
  free: { bg: '#E8F5EE', fg: '#1B7A47' },
  account: { bg: '#E9F0FA', fg: BRAND },
  premium: { bg: '#FDF1DF', fg: '#96631A' },
};

/**
 * What you need in order to *use* something. Deliberately not "what you need to
 * see it" — every one of these pages is readable as a guest, and overstating the
 * gate is the fastest way to lose a visitor who would have stayed.
 */
export const AccessBadge = ({ access }) => {
  const entry = ACCESS[access] ?? ACCESS.guest;
  const tone = badgeTone[entry.tone] ?? badgeTone.free;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 1.25,
        py: 0.375,
        borderRadius: '8px',
        bgcolor: tone.bg,
        color: tone.fg,
        fontSize: '0.75rem',
        fontWeight: 800,
        letterSpacing: 0.2,
        whiteSpace: 'nowrap',
      }}
    >
      {entry.label}
    </Box>
  );
};

/** Body copy colour for the homepage: slate, not the theme's navy. */
export const bodySx = { color: INK, lineHeight: 1.7 };
export const mutedSx = { color: MUTED, lineHeight: 1.7 };

/**
 * A page band. `tone` comes from Home.jsx, which owns the alternation — a section
 * cannot know what the section above it chose, so it must not choose for itself.
 */
export const bandSx = (tone = 'plain') => ({
  bgcolor: tone === 'tint' ? BAND_TINT : BAND_PLAIN,
  py: { xs: 6, md: 10 },
  px: { xs: 2, sm: 4, md: 8 },
});
